import React, { useState, useEffect } from 'react';
import './ScheduleInterviewModal.css';

const ScheduleInterviewModal = ({ isOpen, onClose, candidate, onSchedule, jobId }) => {
    const [formData, setFormData] = useState({
        interviewTitle: `Interview with ${candidate?.candidate_name || 'Candidate'}`,
        interviewerEmail: '',
        interviewType: 'video',
        duration: 60,
        timezone: 'UTC',
        preInterviewNotes: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (candidate && isOpen) {
            setFormData(prev => ({
                ...prev,
                interviewTitle: `Interview with ${candidate.candidate_name || candidate.filename}`
            }));
        }
    }, [candidate, isOpen]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (name === 'interviewerEmail' && error) {
            setError(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);

        try {
            if (!formData.interviewerEmail) {
                setError('Interviewer email is required');
                setIsSubmitting(false);
                return;
            }

            const interviewData = {
                resume_id: candidate.id,
                job_id: jobId,
                interviewer_email: formData.interviewerEmail,
                interview_title: formData.interviewTitle,
                interview_type: formData.interviewType,
                duration_minutes: parseInt(formData.duration, 10),
                timezone: formData.timezone,
                pre_interview_notes: formData.preInterviewNotes
            };

            await onSchedule(interviewData);
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="interview-modal-overlay">
            <div className="interview-modal">
                <div className="interview-modal-header">
                    <h3>Schedule Interview - {candidate?.candidate_name || candidate?.filename}</h3>
                    <button className="close-button" onClick={onClose}>&times;</button>
                </div>

                <div className="interview-modal-content">
                    {error && <div className="error-message">{error}</div>}
                    <form onSubmit={handleSubmit} className="interview-form">
                        <div className="form-group">
                            <label>Interview Title *</label>
                            <input
                                type="text"
                                name="interviewTitle"
                                value={formData.interviewTitle}
                                onChange={handleInputChange}
                                required
                                placeholder="e.g., Technical Interview with John Doe"
                            />
                        </div>

                        <div className="form-group">
                            <label>Interviewer Email *</label>
                            <input
                                type="email"
                                name="interviewerEmail"
                                value={formData.interviewerEmail}
                                onChange={handleInputChange}
                                required
                                placeholder="interviewer@company.com"
                            />
                            <small style={{ color: '#666', fontSize: '12px' }}>
                                Email of the person who will conduct the interview
                            </small>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Interview Type *</label>
                                <select
                                    name="interviewType"
                                    value={formData.interviewType}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="phone">Phone</option>
                                    <option value="video">Video</option>
                                    <option value="onsite">Onsite</option>
                                    <option value="technical">Technical</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Duration (minutes)</label>
                                <select
                                    name="duration"
                                    value={formData.duration}
                                    onChange={handleInputChange}
                                >
                                    <option value={30}>30 minutes</option>
                                    <option value={45}>45 minutes</option>
                                    <option value={60}>1 hour</option>
                                    <option value={90}>1.5 hours</option>
                                    <option value={120}>2 hours</option>
                                </select>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Timezone</label>
                            <select
                                name="timezone"
                                value={formData.timezone}
                                onChange={handleInputChange}
                            >
                                <option value="UTC">UTC</option>
                                <option value="America/New_York">Eastern Time</option>
                                <option value="America/Chicago">Central Time</option>
                                <option value="America/Denver">Mountain Time</option>
                                <option value="America/Los_Angeles">Pacific Time</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Pre-Interview Notes</label>
                            <textarea
                                name="preInterviewNotes"
                                value={formData.preInterviewNotes}
                                onChange={handleInputChange}
                                placeholder="Any notes or context for the interviewer..."
                                rows={4}
                            />
                        </div>

                        <div className="form-actions">
                            <button type="button" onClick={onClose} className="cancel-button">
                                Cancel
                            </button>
                            <button type="submit" className="submit-button" disabled={isSubmitting}>
                                {isSubmitting ? 'Scheduling...' : 'Schedule Interview'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ScheduleInterviewModal;
